import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import AuthenticationRequest from "../../api/requests/AuthenticationRequest";
import LoginProvider from "../../api/enums/LoginProvider";
import AuthClient from "../../api/AuthClient";
import { useLoading } from "../Loader/LoadingContext";
import { localStorageKeys } from "../../utils/localStorageKeys";
import { routes } from "../../routes/routes";
import { GoogleLoginClientId } from "../../utils/constants";
import { Modal } from "react-bootstrap";
import { handleApiError } from "../../api/handleApiErrors";
import styles from "./google.module.scss";
import "../Login/socialLoginButtons.css";
import { getPostLoginRouteForUser } from "../../utils/appMode";

declare global {
  interface Window {
    google?: {
      accounts?: {
        oauth2?: {
          initTokenClient: (config: {
            client_id: string;
            scope: string;
            callback: (response: GoogleTokenResponse) => void;
          }) => {
            requestAccessToken: (options?: { prompt?: string }) => void;
          };
        };
      };
    };
  }
}

type GoogleTokenResponse = {
  access_token?: string;
  expires_in?: number;
  error?: string;
};

export default function GoogleLoginButton() {
  const navigate = useNavigate();
  const { setLoading } = useLoading();
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);

  const handleClose = () => setShowModal(false);
  const handleShow = () => setShowModal(true);

  React.useEffect(() => {
    if (document.getElementById("google-gsi-client")) {
      return;
    }

    const script = document.createElement("script");
    script.id = "google-gsi-client";
    script.src = "https://accounts.google.com/gsi/client";
    script.async = true;
    script.defer = true;
    document.body.appendChild(script);
  }, []);

  const googleLoginCallback = async (response: GoogleTokenResponse) => {
    if (response.error || !response.access_token) {
      setError("Google login failed. Please try again.");
      handleShow();
      return;
    }

    setLoading(true);
    try {
      let firstName: string | undefined;
      let lastName: string | undefined;
      let email: string | undefined;
      let pictureUrl: string | undefined;
      let externalUserId = "";

      const userInfo = await fetch("https://www.googleapis.com/oauth2/v3/userinfo", {
        headers: {
          Authorization: `Bearer ${response.access_token}`,
        },
      });

      if (userInfo.ok) {
        const user = await userInfo.json();
        firstName = user.given_name;
        lastName = user.family_name;
        email = user.email;
        pictureUrl = user.picture;
        externalUserId = user.sub || "";
      }

      const request: AuthenticationRequest = {
        loginProvider: LoginProvider.Google,
        authenticationProviderResponse: {
          firstName,
          lastName,
          email,
          picture: {
            url: pictureUrl,
          },
          externalUserId,
          accessToken: response.access_token,
          expiresIn: response.expires_in || 0,
        },
      };

      const auth: AuthClient = new AuthClient();
      const authResponse = await auth.authenticate(request);
      if (authResponse?.data) {
        localStorage.setItem(localStorageKeys.loginResponse, JSON.stringify(authResponse.data));
        navigate(getPostLoginRouteForUser(authResponse.data.userType));
        return;
      }

      navigate(routes.Root);
    } catch (err) {
      const isHandled = handleApiError(err, setError);
      if (isHandled) {
        handleShow();
      }
    } finally {
      setLoading(false);
    }
  };

  const onGoogleLoginClick = () => {
    const tokenClient = window.google?.accounts?.oauth2?.initTokenClient({
      client_id: GoogleLoginClientId,
      scope: "openid email profile",
      callback: googleLoginCallback,
    });

    if (!tokenClient) {
      setError("Google login is not available right now. Please refresh and try again.");
      handleShow();
      return;
    }

    tokenClient.requestAccessToken({ prompt: "consent" });
  };

  return (
    <>
      <button
        type="button"
        className="loginBtn loginBtn--google"
        onClick={onGoogleLoginClick}
      >
        Login with Google
      </button>
      <Modal show={showModal} backdrop={false} dialogClassName={styles.centeredModal} onHide={handleClose}>
        <Modal.Header closeButton>
          <Modal.Title>Error</Modal.Title>
        </Modal.Header>
        <Modal.Body>{error}</Modal.Body>
      </Modal>
    </>
  );
}
